/* eslint-disable react-hooks/rules-of-hooks */
"use client"

import * as React from "react"
import { useState, useEffect, useCallback } from "react"
import {
  Modal,
  Button,
  Form,
  ListGroup,
  Toast,
  ToastContainer,
  Row,
  Col,
  Card,
  InputGroup,
  Spinner,
  Badge,
} from "react-bootstrap"
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js"
import axios from "axios"
import i18n from "../i18n"
import { axiosInstance } from "../App"
import SuccessModal from "./SuccessModal"


const API_HEADERS ={
  "Content-Type": "application/json",
  "Referrer-Policy": "no-referrer",
  Authorization: "Bearer " + localStorage.getItem("tokenLida"),
  "Accept-Language": i18n.language
}

interface SubscriptionPrice {
  price_id: string
  period: string
  price: number
  currency: string
  description: string
}

interface PaymentMethod {
  id: string
  card: {
    brand: string
    last4: string
    exp_month: number
    exp_year: number
  }
}

interface AddCardFormProps {
  onAddCard: () => Promise<string>
  setToastMessage: (message: string) => void
  setToastVariant: (variant: string) => void
  fetchPaymentMethods: any
}

interface PaymentMethodListProps {
  paymentMethods: PaymentMethod[]
  selectedPaymentMethod: PaymentMethod | null
  onSelect: (method: PaymentMethod) => void
  customerDetails: any
  onAddCard: () => Promise<string>
  setToastMessage: (message: string) => void
  setToastVariant: (variant: string) => void
  fetchPaymentMethods: () => Promise<void>
}


interface StripeModalProps {
  isOpen: boolean;
  onClose: () => void;
}


const StripePaymentModal: React.FC<StripeModalProps> = function({isOpen, onClose}) {
  
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [payAsYouGoAmount, setPayAsYouGoAmount] = useState("30")
  const [payAsYouGoError, setPayAsYouGoError] = useState("")
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [subscriptionPrices, setSubscriptionPrices] = useState<SubscriptionPrice[]>([])
  const [processingPayment, setProcessingPayment] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const [toastVariant, setToastVariant] = useState("success")
  const [customerDetails, setCustomerDetails] = useState<any>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState("")


  const stripe = useStripe()
  const elements = useElements()

  useEffect(function() {
      if (!isOpen) return;
    fetchPaymentMethods()
    fetchSubscriptionPrices()
    fetchCustomerDetails()
  }, [isOpen])

  const fetchPaymentMethods = useCallback(async function() {
    try {
      const response = await fetch(process.env.REACT_APP_API_HOST + "billing/payment-methods", {
        headers: API_HEADERS
      })
      const data = await response.json()
      const methods = data.payment_methods || []
      setPaymentMethods(methods)
      if (methods.length === 1) {
        setSelectedPaymentMethod(methods[0])
      }
    } catch (error) {
      setError("Failed to load payment methods")
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchSubscriptionPrices = useCallback(async function() {
    try {
      const response = await axiosInstance.get(process.env.REACT_APP_API_HOST + "billing/subscription-prices", {
        headers: API_HEADERS
      })
      const prices = response.data.map(function(price: any) {
        return {
          ...price,
          description: price.description || `Our ${price.period}ly plan with great features.`
        }
      })
      setSubscriptionPrices(prices)
    } catch (error) {
      setError("Failed to load subscription prices")
    }
  }, [])

  const fetchCustomerDetails = useCallback(async function() {
    try {
      const response = await axiosInstance.get(process.env.REACT_APP_API_HOST + "billing/customer-details", {
        headers: API_HEADERS
      })
      setCustomerDetails(response.data.customer_details)
    } catch (error) {
      console.error("Error fetching customer details:", error)
    }
  }, [])

  const handleClose = function() {
    setProcessingPayment(false)
    setPayAsYouGoAmount("30")
    setPayAsYouGoError("")
    setSelectedPlan(null)
    onClose()
  }


  const handlePaymentMethodSelect = function(paymentMethod: PaymentMethod) {
    setSelectedPaymentMethod(paymentMethod)
  }

  const handleAddCard = useCallback(async function() {
    try {
      const response = await axiosInstance.post(
        process.env.REACT_APP_API_HOST + "billing/payment-methods/add",
        { type: "card" },
        { headers: API_HEADERS }
      )
      return response.data.clientSecret
    } catch (error) {
      setToastMessage("Failed to initiate add card process")
      setToastVariant("danger")
      setShowToast(true)
      throw error
    }
  }, [])

  const handleSubscribe = useCallback(async function(priceId: string) {
    if (!stripe || !elements) {
      setToastMessage("Stripe is not initialized")
      setToastVariant("danger")
      setShowToast(true)
      return
    }

    setProcessingPayment(true)
    try {
      const createSubscriptionResponse = await axiosInstance.post(
        process.env.REACT_APP_API_HOST + "billing/create-subscription",
        { price_id: priceId },
        { headers: API_HEADERS }
      )

      const { clientSecret } = createSubscriptionResponse.data
      let paymentMethod

      if (!selectedPaymentMethod) {
        const cardElement = elements.getElement(CardElement)
        if (!cardElement) throw new Error("Card details are required")
        
        const { paymentMethod: newPaymentMethod, error } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement
        })
        
        if (error) throw new Error(error.message)
        paymentMethod = newPaymentMethod
      } else {
        paymentMethod = { id: selectedPaymentMethod.id }
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id
      })

      if (result.error) throw new Error(result.error.message)

      if (result.paymentIntent.status === "succeeded") {
        setToastMessage("Successfully subscribed to the plan")
        setToastVariant("success")
        fetchPaymentMethods()
        onClose()
        setSuccessMessage("Successfully subscribed to the plan")
        setShowSuccessModal(true)
      }
    } catch (error: any) {
      setToastMessage(error.message || "Failed to process subscription")
      setToastVariant("danger")
    } finally {
      setProcessingPayment(false)
      setShowToast(true)
    }
  }, [stripe, elements, selectedPaymentMethod, fetchPaymentMethods])

  const handlePayAsYouGoAmountChange = function(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value
    setPayAsYouGoAmount(value)

    if (value === "") {
      setPayAsYouGoError("Amount is required")
    } else if (!/^\d+$/.test(value)) {
      setPayAsYouGoError("Amount must be a number")
    } else if (Number(value) < 10 || Number(value) > 100) {
      setPayAsYouGoError("Amount must be between €10 and €100")
    } else {
      setPayAsYouGoError("")
    }
  }

  const handleBuyCredits = useCallback(async function() {
    if (!stripe || !elements) {
      setToastMessage("Stripe is not initialized")
      setToastVariant("danger")
      setShowToast(true)
      return
    }

    setProcessingPayment(true)
    try {
      const response = await axiosInstance.post(
        process.env.REACT_APP_API_HOST + "billing/add-credit",
        { amount: Number(payAsYouGoAmount) * 100 },
        { headers: API_HEADERS }
      )

      const { clientSecret } = response.data
      let paymentMethod

      if (!selectedPaymentMethod) {
        const cardElement = elements.getElement(CardElement)
        if (!cardElement) throw new Error("Card details are required")
        
        const { paymentMethod: newPaymentMethod, error } = await stripe.createPaymentMethod({
          type: "card",
          card: cardElement
        })
        
        if (error) throw new Error(error.message)
        paymentMethod = newPaymentMethod
      } else {
        paymentMethod = { id: selectedPaymentMethod.id }
      }

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id
      })

      if (result.error) throw new Error(result.error.message)

      if (result.paymentIntent.status === "succeeded") {
        setToastMessage(`Successfully added €${payAsYouGoAmount} credits`)
        setToastVariant("success")
        fetchPaymentMethods()
        onClose()
        setSuccessMessage(`Successfully added €${payAsYouGoAmount} credits`)
        setShowSuccessModal(true)
      }
    } catch (error: any) {
      setToastMessage(error.message || "Failed to process payment")
      setToastVariant("danger")
    } finally {
      setProcessingPayment(false)
      setShowToast(true)
    }
  }, [stripe, elements, selectedPaymentMethod, payAsYouGoAmount, fetchPaymentMethods])

  const formatPrice = function(price: number, currency: string) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency }).format(price)
  }

  const isCardAdded = paymentMethods.length > 0

  // if (loading) return <div>Loading...</div>
  // if (error) return <div>Error: {error}</div>

  return (
    <>
      
      <Modal show={isOpen} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Billing Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PaymentMethodList
            paymentMethods={paymentMethods}
            selectedPaymentMethod={selectedPaymentMethod}
            onSelect={handlePaymentMethodSelect}
            customerDetails={customerDetails}
            onAddCard={handleAddCard}
            setToastMessage={setToastMessage}
            setToastVariant={setToastVariant}
            fetchPaymentMethods={fetchPaymentMethods}
          />
          <Row className="mt-4">
            <Col md={4}>
              <Card className={`h-100 ${payAsYouGoError ? "border-danger" : ""}`}>
                <Card.Body className="d-flex flex-column">
                  <Card.Title>Pay As You Go</Card.Title>
                  <div className="flex-grow-1">
                    <Form.Label className="w-100 text-center mb-3">Amount: €{payAsYouGoAmount}</Form.Label>
                    <div className="custom-slider-container mb-3">
                      <input
                        type="range"
                        min="10"
                        max="100"
                        value={payAsYouGoAmount}
                        onChange={handlePayAsYouGoAmountChange}
                        className="custom-slider"
                      />
                    </div>
                    <InputGroup>
                      <InputGroup.Text>€</InputGroup.Text>
                      <Form.Control
                        type="text"
                        value={payAsYouGoAmount}
                        onChange={handlePayAsYouGoAmountChange}
                        isInvalid={!!payAsYouGoError}
                      />
                    </InputGroup>
                    {payAsYouGoError && <Form.Text className="text-danger">{payAsYouGoError}</Form.Text>}
                  </div>
                  <div className="text-center mt-3">
                    <Button
                      variant="primary"
                      disabled={!isCardAdded || !!payAsYouGoError || payAsYouGoAmount === "" || processingPayment}
                      onClick={handleBuyCredits}
                    >
                      {processingPayment ? <Spinner animation="border" size="sm" /> : "Buy Credits"}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            {subscriptionPrices.map(function(price) {
              return (
                <Col md={4} key={price.price_id}>
                  <Card
                    className={`h-100 subscription-card ${selectedPlan === price.price_id ? "selected" : ""}`}
                    onClick={function() { setSelectedPlan(price.price_id) }}
                  >
                    <Card.Body className="d-flex flex-column">
                      <Card.Title>{price.period.charAt(0).toUpperCase() + price.period.slice(1)}ly Plan</Card.Title>
                      <Card.Text className="mb-2">
                        {formatPrice(price.price, price.currency)} / {price.period}
                      </Card.Text>
                      <Card.Text className="flex-grow-1 mb-3">
                        {price.description || "Enjoy our standard features with this plan."}
                      </Card.Text>
                      <Button
                        variant="primary"
                        className="mt-auto"
                        onClick={function(e) {
                          e.stopPropagation()
                          handleSubscribe(price.price_id)
                        }}
                        disabled={!isCardAdded || processingPayment}
                      >
                        {processingPayment ? <Spinner animation="border" size="sm" /> : "Subscribe"}
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              )
            })}
          </Row>
        </Modal.Body>
      </Modal>
      <SuccessModal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} message={successMessage} />

      <ToastContainer position="top-end" className="p-3">
        <Toast show={showToast} onClose={function() { setShowToast(false) }} delay={3000} autohide bg={toastVariant}>
          <Toast.Header>
            <strong className="me-auto">Notification</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <style>{`
        .subscription-card {
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .subscription-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 4px 8px rgba(0,0,0,0.1);
        }
        .subscription-card.selected {
          border: 2px solid #007bff;
          background-color: #f8f9fa;
        }
        .custom-slider-container {
          width: 100%;
          padding: 10px 0;
        }
        .custom-slider {
          -webkit-appearance: none;
          width: 100%;
          height: 10px;
          border-radius: 5px;
          background: #d3d3d3;
          outline: none;
          opacity: 0.7;
          transition: opacity .2s;
        }
        .custom-slider:hover {
          opacity: 1;
        }
        .custom-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #007bff;
          cursor: pointer;
        }
        .custom-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #007bff;
          cursor: pointer;
        }
      `}</style>
    </>
  )
}

const PaymentMethodList: React.FC<PaymentMethodListProps>  = ({
  paymentMethods,
  selectedPaymentMethod,
  onSelect,
  customerDetails,
  onAddCard,
  setToastMessage,
  setToastVariant,
  fetchPaymentMethods,
}) => {
  const defaultPaymentMethodId = customerDetails?.invoice_settings?.default_payment_method
  const [showAddCardForm, setShowAddCardForm] = useState(false)

  useEffect(() => {
    if (paymentMethods.length === 1) {
      onSelect(paymentMethods[0])
    } else if (paymentMethods.length > 1 && defaultPaymentMethodId) {
      const defaultMethod = paymentMethods.find((method) => method.id === defaultPaymentMethodId)
      if (defaultMethod && !selectedPaymentMethod) {
        onSelect(defaultMethod)
      }
    }
  }, [paymentMethods, onSelect, defaultPaymentMethodId])
  useEffect(() => {
    if (paymentMethods.length === 1) {
      onSelect(paymentMethods[0])
    } else if (paymentMethods.length > 1 && defaultPaymentMethodId) {
      const defaultMethod = paymentMethods.find((method) => method.id === defaultPaymentMethodId)
      if (defaultMethod) {
        onSelect(defaultMethod)
      }
    }
  }, [])

  return (
    <div>
      <h5>Payment Method:</h5>
      {showAddCardForm ? (
        <AddCardForm
          onAddCard={onAddCard}
          setToastMessage={setToastMessage}
          setToastVariant={setToastVariant}
          fetchPaymentMethods={() => {
            fetchPaymentMethods()
            setShowAddCardForm(false)
          }}
        />
      ) : paymentMethods.length === 0 ? (
        <AddCardForm
          onAddCard={onAddCard}
          setToastMessage={setToastMessage}
          setToastVariant={setToastVariant}
          fetchPaymentMethods={fetchPaymentMethods}
        />
      ) : (
        <>
          <p>Select a payment method:</p>
          <ListGroup>
            {paymentMethods.map((method) => (
              <ListGroup.Item
                key={method.id}
                action
                onClick={() => onSelect(method)}
                active={!!selectedPaymentMethod && selectedPaymentMethod.id === method.id}
              >
                {method.card.brand} **** {method.card.last4} (Expires: {method.card.exp_month}/{method.card.exp_year})
                {method.id === defaultPaymentMethodId && (
                  <Badge bg="success" className="ms-2">
                    Default
                  </Badge>
                )}
              </ListGroup.Item>
            ))}
          </ListGroup>
          {paymentMethods.length > 0 && (
            <Button variant="outline-primary" className="mt-3" onClick={() => setShowAddCardForm(true)}>
              Add New Card
            </Button>
          )}
        </>
      )}
      {selectedPaymentMethod && (
        <div className="mt-3">
          <h6>Selected Payment Method:</h6>
          <p>
            {selectedPaymentMethod.card.brand} **** {selectedPaymentMethod.card.last4}
            {selectedPaymentMethod.id === defaultPaymentMethodId && (
              <Badge bg="success" className="ms-2">
                Default
              </Badge>
            )}
          </p>
        </div>
      )}
    </div>
  )
}

const AddCardForm: React.FC<AddCardFormProps> = function({
  onAddCard,
  setToastMessage,
  setToastVariant,
  fetchPaymentMethods
}) {
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const stripe = useStripe()
  const elements = useElements()

  const handleSubmit = async function(event: React.FormEvent) {
    event.preventDefault()
    if (!stripe || !elements) return

    setProcessing(true)
    setError(null)

    const cardElement = elements.getElement(CardElement)
    if (!cardElement) {
      throw new Error('Card element not found')
    }

    try {
      const clientSecret = await onAddCard()
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement }
      })

      if (result.error) throw new Error(result.error.message)

      setToastMessage("Card added successfully")
      setToastVariant("success")
      await fetchPaymentMethods()
    } catch (err: any) {
      setError(err.message)
      setToastMessage("Failed to add card")
      setToastVariant("danger")
    } finally {
      setProcessing(false)
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <CardElement
        options={{
          style: {
            base: {
              fontSize: "16px",
              color: "#424770",
              "::placeholder": {
                color: "#aab7c4",
              },
            },
            invalid: {
              color: "#9e2146",
            },
          },
        }}
      />
      {error && <div className="text-danger mt-2">{error}</div>}
      <Button type="submit" disabled={!stripe || processing} className="mt-3">
        {processing ? "Processing..." : "Add Card"}
      </Button>
    </Form>
  )
}

export default StripePaymentModal